import { MoveRight, ShieldCheck } from 'lucide-react'
import {
	Suspense,
	lazy,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import {
	useActionData,
	useFetcher,
	useLocation,
	useNavigate,
	useSearchParams,
} from 'react-router'
import { NavBar } from '#app/components/nav-bar'
import { Logo } from '#app/components/nav-logo'
import type {
	Property,
	PropertyGroup,
} from '#app/components/screening/PropertySearchSelect'
import { ScreeningConsentModal } from '#app/components/screening/ScreeningConsentModal'
import { useScreeningMode } from '#app/components/screening/ScreeningModeContext'
import { ScreeningProgressBar } from '#app/components/screening/ScreeningProgressBar'
import { DemoIdVerificationStep } from '#app/components/screening/demo/DemoIdVerificationStep'
import { DemoVerifyIncomeStep } from '#app/components/screening/demo/DemoVerifyIncomeStep'
import { FormConfigProvider } from '#app/components/screening/form-config-context'
import { StudentHousingProvider } from '#app/components/screening/student-housing-context'
import { Avatar, AvatarFallback, AvatarImage } from '#app/components/ui/avatar'
import { assets } from '#app/data/assets'
import type { FormKind } from '#app/utils/application-form/catalog'
import {
	type FormConfigV1,
	defaultFormConfig,
} from '#app/utils/application-form/config'
import { createAvatarName } from '#app/utils/avatar-name'
import { cn } from '#app/utils/misc'
import {
	TOTAL_STEPS,
	getNextStep,
	parseStepFromUrl,
} from '#app/utils/screening-progress'
import { useOptionalUser } from '#app/utils/user'

/** Demo: same as org form-config "Your Organization's Terms & Conditions" —
 * enabled + attached PDF so the consent modal requires Landlord T&Cs. */
const DEMO_LANDLORD_TERMS = {
	enabled: true as const,
	fileName: 'Landlord-Terms.pdf',
	blobUrl: '/demo-landlord-terms.pdf',
}

function demoFormConfig(kind: FormKind): FormConfigV1 {
	const base = defaultFormConfig(kind)
	const sections = {
		...base.sections,
		terms: { ...DEMO_LANDLORD_TERMS },
		// Doc dummy: Dog → Weight + Breed below (breed is off in system default).
		pet: {
			...base.sections.pet,
			enabled: true,
			fields: {
				...base.sections.pet.fields,
				amount: true,
				type: true,
				weight: true,
				breed: true,
			},
		},
		// Doc dummy: Car / SUV → plate / make-model / colour (off in system default).
		vehicle: {
			...base.sections.vehicle,
			enabled: true,
			fields: {
				...base.sections.vehicle.fields,
				amount: true,
				type: true,
				license_plate: true,
				make_model: true,
				color: true,
			},
		},
	}
	// Doc: remove "Are You On Any Government Assistance" from student employment.
	if (kind === 'student') {
		sections.employment_questions = {
			...sections.employment_questions,
			prebuilt: {
				...sections.employment_questions.prebuilt,
				government_assistance: false,
			},
		}
	}
	return {
		...base,
		sections,
	}
}

// Lazy load step components for code splitting (same as the screening route)
const PropertyStep = lazy(() =>
	import('#app/components/screening/PropertyStep').then((m) => ({
		default: m.PropertyStep,
	})),
)
const IdVerificationStep = lazy(() =>
	import('#app/components/screening/IdVerificationStep').then((m) => ({
		default: m.IdVerificationStep,
	})),
)
const RentalStep = lazy(() =>
	import('#app/components/screening/RentalStep').then((m) => ({
		default: m.RentalStep,
	})),
)
const EmploymentStep = lazy(() =>
	import('#app/components/screening/EmploymentStep').then((m) => ({
		default: m.EmploymentStep,
	})),
)
const VerifyIncomeStep = lazy(() =>
	import('#app/components/screening/VerifyIncomeStep').then((m) => ({
		default: m.VerifyIncomeStep,
	})),
)
const ReviewStep = lazy(() =>
	import('#app/components/screening/ReviewStep').then((m) => ({
		default: m.ReviewStep,
	})),
)

type ActionData =
	| {
			success: true
			nextStep: number
			applicationId: string
			/** Student Bank=No — verify-income marked skipped in this response. */
			skipVerifyIncome?: boolean
			/** Employment saved without a skip — income is required again. */
			clearVerifyIncomeSkip?: boolean
	  }
	| { error: string }
	| null

function applyVerifyIncomeSkip(
	prev: {
		stepData: Record<string, Record<string, unknown>>
		completedSteps: string[]
	},
	result: {
		skipVerifyIncome?: boolean
		clearVerifyIncomeSkip?: boolean
	},
) {
	const nextStepData = { ...prev.stepData }
	let nextCompleted = [...prev.completedSteps]

	if (result.skipVerifyIncome) {
		nextStepData['verify-income'] = {
			...(nextStepData['verify-income'] ?? {}),
			completed: true,
			skipped: true,
			skipReason: 'no_canadian_bank_account',
			hasCanadianBankAccount: 'no',
			accounts: [],
		}
		if (!nextCompleted.includes('verify-income')) {
			nextCompleted = [...nextCompleted, 'verify-income']
		}
		if (!nextCompleted.includes('employment')) {
			nextCompleted = [...nextCompleted, 'employment']
		}
	} else if (result.clearVerifyIncomeSkip) {
		const previous = nextStepData['verify-income'] ?? {}
		nextStepData['verify-income'] = {
			...previous,
			completed: false,
			skipped: false,
			skipReason: null,
			accounts: [],
		}
		nextCompleted = nextCompleted.filter((step) => step !== 'verify-income')
	}

	return { stepData: nextStepData, completedSteps: nextCompleted }
}

/**
 * The data a screening flow needs to render. Both the live screening route
 * (/tenant/screening) and the public demo route (/demo) build this same shape
 * — the live route from the DB, the demo route from hardcoded mock data — so
 * the rendered flow is byte-for-byte identical between them.
 */
export interface ScreeningFlowData {
	userId: string
	applicationId: string | null
	currentStep: number
	stepNumberToName: Record<number, string>
	propertyGroups?: PropertyGroup[]
	properties?: Property[]
	propertyUnits?: Record<
		string,
		Array<{
			id?: string
			unitNumber?: string | null
			bedrooms: number | null
			bathrooms: number | null
			beds?: Array<{ id: string; label: string }>
		}>
	>
	stepData?: Record<string, Record<string, unknown>>
	/** Demo-only: a complete dataset the Review step reads instead of `stepData`.
	 * The demo's steps start collapsed / unselected and persist nothing, so their
	 * stepData can't drive a populated review — this fills that gap. Unused in the
	 * live flow (the review reads the DB-backed `stepData`). */
	reviewStepData?: Record<string, Record<string, unknown>>
	hasConsent?: boolean
	completedSteps?: string[]
	applicationStatus?: string | null
	// Loosely typed because the live loader returns a raw `string` from the DB;
	// the flow narrows it to the union via `typedApplicationType` below.
	applicationType?: string
	googleMapsApiKey?: string
	allowOnlyStep?: string
	updateConsentToken?: string
	primaryApplicantInfo?: {
		name: string
		propertyName: string
		propertyAddress: string
		occupancy?: Record<string, unknown>
	} | null
	incomeReassuranceCallTriggered?: boolean
	/** Name of the person going through the flow, shown in the consent modal and
	 * guarantor landing header. The live flow derives this from the logged-in
	 * user; the public demo (no auth session) passes it explicitly so those
	 * headers match the live flow. */
	viewerName?: string
	/**
	 * Application-form template kind. Demo uses this to switch between the
	 * standard and student FormConfig; live screening usually pins a snapshot.
	 * Defaults to `standard`.
	 */
	formKind?: FormKind
	/**
	 * When true, Property/Employment/Rental render student-housing fields
	 * (school/program, multi-guarantors, unit+bed, student assistance, etc.).
	 */
	isStudentHousing?: boolean
}

/**
 * Shared screening orchestration shell.
 *
 * This is the exact step machine that used to live in
 * `app/routes/tenant+/screening.tsx`'s default export: consent gating, step
 * switching, URL <-> step sync, edit-mode, and the progress bar. It is now a
 * standalone component so the live route and the demo route render the SAME
 * flow. The only behavioral fork is `mode` (from ScreeningModeContext): in
 * `demo` mode the ID and Verify-Income steps are swapped for simulated
 * variants that never touch Veriff/DB.
 */
export function ScreeningFlow({
	userId,
	applicationId: initialApplicationId,
	currentStep: initialStep,
	stepNumberToName,
	propertyGroups = [],
	properties: allProperties = [],
	propertyUnits: propertyUnitsData = {},
	stepData: initialStepData = {},
	reviewStepData,
	hasConsent: initialHasConsent = false,
	googleMapsApiKey,
	completedSteps: initialCompletedSteps = [],
	applicationStatus: initialApplicationStatus = null,
	applicationType = 'primary',
	allowOnlyStep,
	updateConsentToken,
	primaryApplicantInfo,
	incomeReassuranceCallTriggered = false,
	viewerName,
	formKind = 'standard',
	isStudentHousing = false,
}: ScreeningFlowData) {
	const { mode } = useScreeningMode()
	const isDemo = mode === 'demo'
	const formConfig = useMemo(
		() => (isDemo ? demoFormConfig(formKind) : defaultFormConfig(formKind)),
		[isDemo, formKind],
	)
	const landlordTerms =
		formConfig.sections.terms.enabled && formConfig.sections.terms.blobUrl
			? {
					blobUrl: formConfig.sections.terms.blobUrl,
					fileName: formConfig.sections.terms.fileName,
				}
			: null
	const currentUser = useOptionalUser()
	// In the demo there is no auth session, so fall back to the explicit
	// viewerName so the guarantor landing header matches the live flow.
	const guarantorDisplayName =
		[currentUser?.profile?.firstName, currentUser?.profile?.lastName]
			.filter(Boolean)
			.join(' ')
			.trim() ||
		currentUser?.name ||
		viewerName ||
		''
	const actionData = useActionData<ActionData>()
	const consentFetcher = useFetcher<ActionData>()
	const navigate = useNavigate()
	const location = useLocation()
	const [searchParams] = useSearchParams()
	const urlSyncedRef = useRef(false)

	// Check URL for edit mode parameter
	const urlEditMode = searchParams.get('edit') === 'true'

	const typedApplicationType = applicationType as
		| 'primary'
		| 'co_applicant'
		| 'guarantor'
	const isGuarantor = typedApplicationType === 'guarantor'

	const [screeningState, setScreeningState] = useState({
		currentStep: initialStep,
		completedSteps: initialCompletedSteps,
		stepData: initialStepData,
		hasConsent: initialHasConsent,
		showIntro: false,
		showConsent: !initialHasConsent,
		showGuarantorLanding: isGuarantor && !initialHasConsent,
		applicationId: initialApplicationId,
		isEditMode: urlEditMode,
		previousStep: initialStep,
	})

	// Demo-only: whether income has been verified. Kept in the flow shell (not the
	// step) so it survives leaving and re-entering the Verify Income step — the
	// demo persists nothing, so the step's own fetcher state is lost on unmount.
	const [demoIncomeVerified, setDemoIncomeVerified] = useState(false)

	// Sync URL with applicationId on initial load if it exists but isn't in URL
	// Use ref to prevent double loading
	useEffect(() => {
		// Only sync once, and only if applicationId exists but isn't in URL
		if (
			!urlSyncedRef.current &&
			initialApplicationId &&
			!searchParams.get('applicationId')
		) {
			urlSyncedRef.current = true // Mark as synced before navigation

			const newSearchParams = new URLSearchParams(searchParams)
			newSearchParams.set('applicationId', initialApplicationId)
			const stepName = stepNumberToName[initialStep]
			if (stepName) {
				newSearchParams.set('step', stepName)
			}
			void navigate(`?${newSearchParams.toString()}`, { replace: true })
		}
		// Reset ref when applicationId changes (new application)
		if (
			initialApplicationId &&
			searchParams.get('applicationId') !== initialApplicationId
		) {
			urlSyncedRef.current = false
		}
	}, [
		initialApplicationId,
		initialStep,
		navigate,
		searchParams,
		stepNumberToName,
	])

	// Track programmatic navigation to prevent URL sync loop
	const isNavigatingRef = useRef(false)
	const prevActionDataRef = useRef(actionData)

	// Update state when action completes and sync URL
	useEffect(() => {
		// Only process if actionData exists, is successful, and hasn't been processed yet
		if (
			actionData &&
			actionData !== prevActionDataRef.current &&
			'success' in actionData &&
			actionData.success
		) {
			if ('nextStep' in actionData && actionData.nextStep) {
				// Mark that we've processed this actionData
				prevActionDataRef.current = actionData

				// Mark that we're navigating programmatically
				isNavigatingRef.current = true

				const newApplicationId =
					actionData.applicationId || screeningState.applicationId
				const nextStep = actionData.nextStep

				// Update URL with applicationId and step
				const newSearchParams = new URLSearchParams(searchParams)
				if (newApplicationId) {
					newSearchParams.set('applicationId', newApplicationId)
				}
				const stepName = stepNumberToName[nextStep]
				if (stepName) {
					newSearchParams.set('step', stepName)
				}
				// Clear edit mode when navigating to review step
				if (nextStep === 6) {
					newSearchParams.delete('edit')
				}
				void navigate(`?${newSearchParams.toString()}`, { replace: true })

				// Update state atomically
				setScreeningState((prev) => {
					const { stepData: nextStepData, completedSteps: nextCompleted } =
						applyVerifyIncomeSkip(prev, actionData)

					return {
						...prev,
						currentStep: nextStep,
						previousStep: prev.currentStep,
						applicationId: newApplicationId,
						hasConsent: newApplicationId ? true : prev.hasConsent,
						showConsent: false,
						stepData: nextStepData,
						completedSteps: nextCompleted,
						// Reset edit mode when navigating to review step or when action completes
						isEditMode: nextStep === 6 ? false : prev.isEditMode,
					}
				})

				// Reset navigation flag after a short delay to allow URL update to complete
				setTimeout(() => {
					isNavigatingRef.current = false
				}, 100)
			}
		}
	}, [
		actionData,
		screeningState.applicationId,
		stepNumberToName,
		navigate,
		searchParams,
	])

	// Memoized navigation functions (for manual navigation like back button)
	const navigateToStep = useCallback(
		(step: number, isEditModeNavigation = false) => {
			// Mark that we're navigating programmatically
			isNavigatingRef.current = true

			setScreeningState((prev) => {
				const previousStep = prev.currentStep
				// Determine if we're entering edit mode (coming from review step 6)
				const isEditMode =
					isEditModeNavigation || (previousStep === 6 && step !== 6)
				// Reset edit mode when navigating to review step
				const shouldResetEditMode = step === 6

				const newState = {
					...prev,
					currentStep: step,
					previousStep,
					isEditMode: shouldResetEditMode ? false : isEditMode,
				}

				// Update URL with step if we have applicationId
				if (newState.applicationId) {
					const currentParams = new URLSearchParams(window.location.search)
					currentParams.set('applicationId', newState.applicationId)
					const stepName = stepNumberToName[step]
					if (stepName) {
						currentParams.set('step', stepName)
					}
					if (isEditMode) {
						currentParams.set('edit', 'true')
					} else {
						currentParams.delete('edit')
					}
					void navigate(`?${currentParams.toString()}`, { replace: true })
				}

				// Reset navigation flag after a short delay
				setTimeout(() => {
					isNavigatingRef.current = false
				}, 100)

				return newState
			})
		},
		[stepNumberToName, navigate],
	)

	const goNext = useCallback(
		(nextStep?: number) => {
			if (nextStep !== undefined) {
				if (nextStep <= TOTAL_STEPS) {
					navigateToStep(nextStep)
				}
			} else {
				setScreeningState((prev) => {
					const step = prev.currentStep + 1
					if (step <= TOTAL_STEPS) {
						return { ...prev, currentStep: step }
					}
					return prev
				})
			}
		},
		[navigateToStep],
	)

	const goBack = useCallback(
		(prevStep?: number) => {
			if (prevStep !== undefined) {
				if (prevStep >= 1) {
					navigateToStep(prevStep)
				}
			} else {
				setScreeningState((prev) => {
					const step = prev.currentStep - 1
					if (step >= 1) {
						return { ...prev, currentStep: step }
					}
					return prev
				})
			}
		},
		[navigateToStep],
	)

	// Handle consent using fetcher
	const handleConsent = useCallback(() => {
		const formData = new FormData()
		formData.append('_action', 'consent')
		if (screeningState.applicationId) {
			formData.append('applicationId', screeningState.applicationId)
		}
		formData.append('consentVeriff', 'true')
		formData.append('consentFlinks', 'true')
		formData.append('consentTenCheck', 'true')
		formData.append('consentContact', 'true')
		formData.append('consentTerms', 'true')
		formData.append('consentPrivacy', 'true')

		void consentFetcher.submit(formData, { method: 'POST' })
	}, [screeningState.applicationId, consentFetcher])

	// Sync stepData with loader data when it changes (to get updated data after edits)
	useEffect(() => {
		// Live revalidation returns the saved step from the database. The demo has
		// no persistence layer, so replacing its in-memory edits with the static
		// loader fixture here would reset numChildren before Employment renders.
		if (isDemo) return
		if (initialStepData && Object.keys(initialStepData).length > 0) {
			setScreeningState((prev) => ({
				...prev,
				stepData: initialStepData,
			}))
		}
	}, [initialStepData, isDemo])

	// Sync completedSteps from loader so the URL-step guard has up-to-date data
	useEffect(() => {
		if (initialCompletedSteps) {
			setScreeningState((prev) => {
				const prevSet = new Set(prev.completedSteps)
				const newSet = new Set(initialCompletedSteps)
				if (
					prevSet.size === newSet.size &&
					[...prevSet].every((s) => newSet.has(s))
				) {
					return prev
				}
				return { ...prev, completedSteps: initialCompletedSteps }
			})
		}
	}, [initialCompletedSteps])

	// Sync current step with URL parameter when it changes (e.g., direct navigation to review step)
	// Only sync if URL step differs from current step and the step is accessible
	// Uses initialCompletedSteps (from loader) to avoid stale local state blocking navigation
	useEffect(() => {
		const urlStepParam = searchParams.get('step')
		if (urlStepParam && !isNavigatingRef.current) {
			const urlStepNumber = parseStepFromUrl(urlStepParam)
			if (urlStepNumber && urlStepNumber !== screeningState.currentStep) {
				// In demo mode there is no DB-backed completion, so don't gate URL
				// navigation on completedSteps — the prefilled flow is fully browsable.
				const maxAllowed = isDemo
					? TOTAL_STEPS
					: getNextStep(initialCompletedSteps, 1)
				if (urlStepNumber > maxAllowed) {
					return
				}
				setScreeningState((prev) => ({
					...prev,
					currentStep: urlStepNumber,
					previousStep: prev.currentStep,
					isEditMode: urlStepNumber === 6 ? false : prev.isEditMode,
				}))
			}
		}
	}, [searchParams, screeningState.currentStep, initialCompletedSteps, isDemo])

	// Handle consent fetcher response
	useEffect(() => {
		if (consentFetcher.state === 'idle' && consentFetcher.data) {
			if (
				consentFetcher.data &&
				'success' in consentFetcher.data &&
				consentFetcher.data.success
			) {
				const response = consentFetcher.data

				const newApplicationId =
					response.applicationId || screeningState.applicationId

				// Update URL with applicationId
				if (newApplicationId) {
					const currentParams = new URLSearchParams(window.location.search)
					currentParams.set('applicationId', newApplicationId)
					void navigate(`?${currentParams.toString()}`, { replace: true })
				}

				setScreeningState((prev) => ({
					...prev,
					hasConsent: true,
					showConsent: false,
					applicationId: newApplicationId,
					currentStep: response.nextStep || prev.currentStep,
				}))
			}
		}
	}, [
		consentFetcher.state,
		consentFetcher.data,
		screeningState.applicationId,
		navigate,
	])

	// Memoized step renderer
	const stepComponent = useMemo(() => {
		const currentStep = screeningState.currentStep
		const isEditMode = screeningState.isEditMode

		// Determine step name and completion status
		const stepName = stepNumberToName[currentStep] || ''
		const isStepCompleted = screeningState.completedSteps.includes(stepName)

		// Determine back button handler
		const getBackHandler = () => {
			if (isStepCompleted && !isEditMode) {
				return undefined // Block back button
			}
			if (isEditMode) {
				return () => navigateToStep(6) // Go back to review step
			}
			// Normal back navigation
			if (currentStep === 1) return undefined
			return () => goBack(currentStep - 1)
		}

		// Determine next button handler
		const getNextHandler = (nextStep: number) => {
			return () => goNext(nextStep)
		}

		switch (currentStep) {
			case 1:
				return (
					<PropertyStep
						currentStep={1}
						onNext={getNextHandler(2)}
						onBack={getBackHandler()}
						userId={userId}
						applicationId={screeningState.applicationId}
						stepData={screeningState.stepData.property}
						propertyGroups={propertyGroups}
						properties={allProperties}
						propertyUnits={propertyUnitsData}
						isEditMode={isEditMode}
						isStepCompleted={isStepCompleted}
						onStepDataChange={
							isDemo
								? (propertyData) =>
										setScreeningState((prev) => ({
											...prev,
											stepData: {
												...prev.stepData,
												property: {
													...prev.stepData.property,
													...propertyData,
												},
											},
										}))
								: undefined
						}
						updateConsentToken={updateConsentToken}
						isCoApplicant={
							applicationType === 'co_applicant' ||
							applicationType === 'guarantor'
						}
						applicationType={typedApplicationType}
						primaryApplicantInfo={primaryApplicantInfo}
					/>
				)
			case 2:
				return isDemo ? (
					<DemoIdVerificationStep
						onNext={getNextHandler(3)}
						onBack={getBackHandler()}
					/>
				) : (
					<IdVerificationStep
						currentStep={2}
						onNext={getNextHandler(3)}
						onBack={getBackHandler()}
						userId={userId}
						applicationId={screeningState.applicationId}
						stepData={screeningState.stepData['id-verification']}
						isEditMode={isEditMode}
						isStepCompleted={isStepCompleted}
					/>
				)
			case 3:
				return (
					<RentalStep
						currentStep={3}
						onNext={getNextHandler(4)}
						onBack={getBackHandler()}
						userId={userId}
						applicationId={screeningState.applicationId}
						stepData={screeningState.stepData.rental}
						googleMapsApiKey={googleMapsApiKey}
						isEditMode={isEditMode}
						isStepCompleted={isStepCompleted}
						updateConsentToken={updateConsentToken}
						applicationType={typedApplicationType}
					/>
				)
			case 4:
				return (
					<EmploymentStep
						currentStep={4}
						onNext={getNextHandler(5)}
						onBack={getBackHandler()}
						userId={userId}
						applicationId={screeningState.applicationId}
						propertyStepData={screeningState.stepData.property}
						stepData={screeningState.stepData.employment}
						googleMapsApiKey={googleMapsApiKey}
						isEditMode={isEditMode}
						isStepCompleted={isStepCompleted}
						updateConsentToken={updateConsentToken}
						verifyIncomeSkipped={
							screeningState.stepData['verify-income']?.skipped === true
						}
						onEmploymentActionResult={(result) => {
							setScreeningState((prev) => {
								const applied = applyVerifyIncomeSkip(prev, result)
								return { ...prev, ...applied }
							})
						}}
					/>
				)
			case 5:
				return isDemo ? (
					<DemoVerifyIncomeStep
						onNext={getNextHandler(6)}
						onBack={getBackHandler()}
						previousStep={screeningState.previousStep}
						isVerified={demoIncomeVerified}
						onVerified={() => setDemoIncomeVerified(true)}
					/>
				) : (
					<VerifyIncomeStep
						currentStep={5}
						onNext={getNextHandler(6)}
						onBack={getBackHandler()}
						userId={userId}
						applicationId={screeningState.applicationId}
						stepData={screeningState.stepData['verify-income']}
						isEditMode={isEditMode}
						isStepCompleted={isStepCompleted}
						incomeReassuranceCallTriggered={incomeReassuranceCallTriggered}
						applicationType={typedApplicationType}
					/>
				)
			case 6: {
				// Demo persists nothing, so the review reads a complete dataset when
				// one is supplied (reviewStepData); the live flow uses the DB-backed
				// stepData as before.
				const incomeSkipped =
					screeningState.stepData['verify-income']?.skipped === true
				const reviewData = reviewStepData
					? {
							...reviewStepData,
							// Demo fixture includes connected banks. A Bank=No skip
							// must replace that so Review shows the skipped state.
							...(incomeSkipped
								? {
										'verify-income': {
											completed: true,
											skipped: true,
											skipReason: 'no_canadian_bank_account',
											hasCanadianBankAccount: 'no',
											accounts: [],
										},
									}
								: {}),
						}
					: screeningState.stepData
				return (
					<ReviewStep
						currentStep={6}
						onBack={() => goBack(incomeSkipped ? 4 : 5)}
						userId={userId}
						applicationId={screeningState.applicationId}
						stepData={reviewData.review}
						allStepData={reviewData}
						onStepChange={navigateToStep}
						applicationStatus={initialApplicationStatus}
						applicationType={typedApplicationType}
						primaryApplicantInfo={primaryApplicantInfo}
					/>
				)
			}
			default:
				return (
					<div className="min-h-screen bg-gray-50 px-4 py-8">
						<div className="mx-auto max-w-4xl">
							<p>Invalid step</p>
						</div>
					</div>
				)
		}
	}, [
		screeningState.currentStep,
		screeningState.stepData,
		reviewStepData,
		screeningState.applicationId,
		screeningState.isEditMode,
		screeningState.completedSteps,
		userId,
		goNext,
		goBack,
		navigateToStep,
		initialApplicationStatus,
		propertyGroups,
		allProperties,
		propertyUnitsData,
		googleMapsApiKey,
		stepNumberToName,
		applicationType,
		typedApplicationType,
		updateConsentToken,
		primaryApplicantInfo,
		incomeReassuranceCallTriggered,
		isDemo,
		demoIncomeVerified,
	])

	// Watch for iframe mode to conditionally hide progress bar
	const [isIframeOpen, setIsIframeOpen] = useState(false)

	useEffect(() => {
		const checkIframeMode = () => {
			setIsIframeOpen(document.body.classList.contains('flinks-iframe-open'))
		}

		// Check initially
		checkIframeMode()

		// Watch for class changes using MutationObserver
		const observer = new MutationObserver(checkIframeMode)
		observer.observe(document.body, {
			attributes: true,
			attributeFilter: ['class'],
		})

		return () => observer.disconnect()
	}, [])

	// Same flag staging sets from the screening route while consent is open.
	// Stable callback so the attribute is not cleared on every re-render.
	const blurConsentHeader = useCallback(() => {
		document.body.setAttribute('data-navbar-blur', 'true')
		return () => document.body.removeAttribute('data-navbar-blur')
	}, [])

	// Show error if there is one
	if (actionData && 'error' in actionData) {
		return (
			<div className="min-h-screen bg-gray-50 px-4 py-8">
				<div className="mx-auto max-w-4xl">
					<div className="rounded-lg border border-red-200 bg-red-50 p-8 text-center">
						<h1 className="mb-4 text-2xl font-medium text-red-800">Error</h1>
						<p className="mb-4 text-red-600">{actionData.error}</p>
					</div>
				</div>
			</div>
		)
	}

	// In the demo flow the shared marketing NavBar is only wanted on the two
	// pre-step screens — the guarantor landing and the consent modal — exactly
	// like the live route, where the global NavBar (z-50) shows through the z-40
	// overlays. The live route renders NavBar globally, so this is demo-only.
	// `hideToggle` drops the Tenant/Landlord switch (screening context).
	// Decorative only on the two pre-step screens: `pointer-events-none` makes the
	// whole bar non-interactive (no logo link, no user dropdown) — it's just chrome
	// matching the live flow, not a working nav.
	// Live screening (staging / pre-prod) frosts the shared NavBar from the
	// route while the consent modal is open (`data-navbar-blur`). Demo has no
	// global screening route, so the demo consent screen sets the same flag.
	// Guarantor landing stays crisp, matching staging.
	const demoNavBar = (blurHeader: boolean) =>
		isDemo ? (
			<div
				className="pointer-events-none"
				ref={blurHeader ? blurConsentHeader : undefined}
			>
				<NavBar
					isLoggedIn={true}
					routeType="marketing"
					setSidebarOpen={() => {}}
					theme={null}
					hideToggle
					// Public demo has no auth session — show the persona in the user
					// dropdown instead of an empty avatar.
					viewerName={guarantorDisplayName || viewerName}
				/>
			</div>
		) : null

	// Guarantor landing page - shown before consent
	if (screeningState.showGuarantorLanding && isGuarantor) {
		const guarantorPropertyStep = screeningState.stepData?.property as
			| Record<string, unknown>
			| undefined
		const guarantorPropertyLabel =
			primaryApplicantInfo?.propertyName ||
			(guarantorPropertyStep?.propertyName as string) ||
			primaryApplicantInfo?.propertyAddress ||
			(guarantorPropertyStep?.address as string) ||
			''
		return (
			<>
				{/* Demo-only shared NavBar, sitting ABOVE the overlay exactly like the
				    live route (global NavBar z-50 over the z-40 consent overlay). The
				    overlay drops to z-40 in demo so the header shows through. */}
				{demoNavBar(false)}
				<div
					className={cn(
						'fixed inset-0 overflow-hidden',
						isDemo ? 'z-40' : 'z-50',
					)}
				>
					{/* Property image background */}
					<div
						className="absolute inset-0 scale-105 bg-cover bg-center"
						style={{
							backgroundImage: `url('${assets.screening.consentBackground2}')`,
						}}
					/>

					{/* #32302F 50% veil over the background image only */}
					{/* <div className="pointer-events-none absolute inset-0 z-20 bg-[#32302F]/50" /> */}

					{/* Centered guarantor card, above the veil */}
					<div className="absolute inset-0 z-30 flex items-center justify-center overflow-y-auto p-4">
						<div className="relative mt-10 w-full max-w-2xl rounded-[30px] bg-[#FBFDFD] px-8 py-12 pb-8 text-center shadow-xl">
							<div className="absolute -top-[30px] left-1/2 flex h-16 w-16 -translate-x-1/2 items-center justify-center rounded-full border border-[#16A354] bg-[#F0FDF4]">
								<ShieldCheck className="h-8 w-8 text-[#16A354]" />
							</div>
							<p className="text-[18px] leading-relaxed text-[#32302F] sm:text-[20px]">
								You have been requested to be a guarantor for <br />
								<span className="font-[500]">
									{primaryApplicantInfo?.name || 'the applicant'}
								</span>
								{guarantorPropertyLabel ? (
									<>
										{' '}
										at{' '}
										<span className="font-[500]">{guarantorPropertyLabel}</span>
									</>
								) : (
									'.'
								)}
							</p>

							<div className="mt-8 flex justify-center">
								<button
									type="button"
									onClick={() => {
										setScreeningState((prev) => ({
											...prev,
											showGuarantorLanding: false,
										}))
									}}
									className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-[#32302F] px-8 py-3 text-[16px] font-medium text-white transition-colors hover:bg-[#484848]"
								>
									Start Screening
									<MoveRight />
								</button>
							</div>
						</div>
					</div>
				</div>
			</>
		)
	}

	// Show consent modal if intro completed but no consent
	if (screeningState.showConsent) {
		return (
			<>
				{demoNavBar(true)}
				<ScreeningConsentModal
					onConsent={handleConsent}
					isSubmitting={consentFetcher.state === 'submitting'}
					landlordTerms={landlordTerms}
					onClose={() => {
						if (isGuarantor) {
							setScreeningState((prev) => ({
								...prev,
								showGuarantorLanding: true,
							}))
						} else if (isDemo) {
							// Demo has no app history / dashboard — just dismiss back to the
							// public demo landing (step 0 is owned by the demo route shell).
							void navigate('/demo')
						} else if (location.key === 'default') {
							// Landed here directly (e.g. from an invitation/email link) — there
							// is no in-app history to go back to, so send them to their
							// applications dashboard instead.
							void navigate('/applications')
						} else {
							window.history.back()
						}
					}}
				/>
			</>
		)
	}

	return (
		<FormConfigProvider kind={formKind} config={formConfig}>
			<StudentHousingProvider isStudentHousing={isStudentHousing}>
				<div
					className="min-h-screen bg-[#FBFDFD]"
					// The demo consent/landing NavBar sets --navbar-height while mounted and
					// doesn't restore it on unmount, which would leave a top gap on the
					// steps. Force it back to 0 here so the steps render flush as before.
					ref={
						isDemo
							? () => {
									document.documentElement.style.setProperty(
										'--navbar-height',
										'0px',
									)
								}
							: undefined
					}
				>
					{/* Progress Bar - hidden when iframe is open or in single-step update (consent) mode */}
					{!isIframeOpen && !allowOnlyStep && (
						<ScreeningProgressBar
							currentStep={screeningState.currentStep}
							completedSteps={screeningState.completedSteps}
							skippedSteps={
								screeningState.stepData['verify-income']?.skipped === true
									? ['verify-income']
									: []
							}
							isGuarantor={isGuarantor}
						/>
					)}

					{/* Step Content with Suspense for lazy loading */}
					<Suspense
						fallback={
							<div className="flex min-h-screen items-center justify-center">
								<div className="text-base text-[#484848]">Loading...</div>
							</div>
						}
					>
						{stepComponent}
					</Suspense>
				</div>
			</StudentHousingProvider>
		</FormConfigProvider>
	)
}
